import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  ChevronDown,
  Check,
  Shirt,
  AlertTriangle,
  User,
  BookOpen,
  Trash2,
  Tag,
  Loader2,
  Plus,
  AlertCircle,
} from "lucide-react";

const INTERVENTION_CHOICES = [
  { value: "verbal", label: "Verbal Warning" },
  { value: "telegram", label: "Telegram Parent Notice" },
  { value: "call", label: "Parent Phone Call" },
  { value: "detention", label: "Detention / Escalation" },
];

const DEFAULT_ITEM_PRESETS = [
  "No ID Card",
  "No Tie",
  "No Scarf",
  "No PE Uniform",
  "Wrong Shoes",
  "Wrong Hair Color",
  "No School Logo",
  "Wrong Shirt Sleeves",
  "Wrong ID string",
];

const getLocalDateTimeString = (dateInput) => {
  const date = dateInput ? new Date(dateInput) : new Date();
  const pad = (num) => String(num).padStart(2, "0");
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const CustomSelect = ({
  label,
  options = [],
  value,
  onChange,
  placeholder = "Select...",
  icon: Icon,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const normalizedOptions = options.map((opt) => {
    if (typeof opt === "object" && opt !== null) {
      if ("value" in opt && "label" in opt) return opt;

      const studentName =
        opt.name ||
        `${opt.last_name || ""}, ${opt.first_name || ""}`.trim() ||
        `Student #${opt.id}`;

      return {
        value: opt.id || opt.student_id,
        label: studentName,
      };
    }

    return { value: opt, label: opt };
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = normalizedOptions.find(
    (opt) => String(opt.value) === String(value)
  );

  return (
    <div className="relative flex flex-col gap-1 w-full" ref={dropdownRef}>
      {label && (
        <label className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wider">
          {label}
        </label>
      )}
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen((prev) => !prev)}
        className={`w-full h-9 px-3 border rounded-lg text-xs font-medium flex items-center justify-between transition-colors ${
          disabled
            ? "bg-zinc-100 text-zinc-500 border-zinc-200 cursor-not-allowed"
            : "bg-zinc-50 border-zinc-200 text-zinc-800 hover:bg-zinc-100/80 focus:outline-none focus:border-zinc-400"
        }`}
      >
        <span className="flex items-center gap-2 truncate">
          {Icon && <Icon className="w-3.5 h-3.5 text-zinc-400 shrink-0" />}
          <span className={selectedOption ? "text-zinc-800" : "text-zinc-400"}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
        </span>
        {!disabled && (
          <ChevronDown
            className={`w-3.5 h-3.5 text-zinc-400 transition-transform duration-200 ${
              isOpen ? "rotate-180 text-zinc-700" : ""
            }`}
          />
        )}
      </button>

      <AnimatePresence>
        {isOpen && !disabled && (
          <motion.ul
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            className="absolute top-full left-0 right-0 mt-1 z-50 max-h-48 overflow-y-auto bg-white border border-zinc-200 rounded-lg shadow-lg py-1 text-xs"
          >
            {normalizedOptions.length > 0 ? (
              normalizedOptions.map((opt) => {
                const isSelected = String(opt.value) === String(value);
                return (
                  <li
                    key={opt.value}
                    onClick={() => {
                      onChange(opt.value);
                      setIsOpen(false);
                    }}
                    className={`px-3 py-2 flex items-center justify-between cursor-pointer transition-colors ${
                      isSelected
                        ? "bg-zinc-100 text-zinc-900 font-semibold"
                        : "hover:bg-zinc-50 text-zinc-700"
                    }`}
                  >
                    <span className="truncate">{opt.label}</span>
                    {isSelected && (
                      <Check className="w-3.5 h-3.5 text-zinc-700 shrink-0" />
                    )}
                  </li>
                );
              })
            ) : (
              <li className="px-3 py-2 text-zinc-400 italic text-center">
                No options available
              </li>
            )}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
};

const LogUniformInfractionModal = ({
  isOpen,
  onClose,
  onSubmit,
  onClear,
  students = [],
  subjects = [],
  existingTodayInfractions = [],
  selectedStudentId = "",
  selectedSubjectId = "",
  existingInfraction = null,
  apiError = null,
  onShowToast,
}) => {
  const [formData, setFormData] = useState({
    student: "",
    subject: "",
    specific_items: [],
    intervention_applied: "verbal",
    date_of_infraction: getLocalDateTimeString(),
    detailed_note: "",
  });

  const [availablePresets, setAvailablePresets] = useState(DEFAULT_ITEM_PRESETS);
  const [customInput, setCustomInput] = useState("");
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsSubmitting(false);
      setCustomInput("");

      if (existingInfraction) {
        let parsedItems = [];
        if (Array.isArray(existingInfraction.specific_item)) {
          parsedItems = existingInfraction.specific_item;
        } else if (typeof existingInfraction.specific_item === "string") {
          parsedItems = existingInfraction.specific_item
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean);
        }

        // Merge custom existing items into preset list if missing
        setAvailablePresets((prev) => {
          const combined = new Set([...prev, ...parsedItems]);
          return Array.from(combined);
        });

        setFormData({
          id: existingInfraction.id,
          student: String(existingInfraction.student || selectedStudentId || ""),
          subject: existingInfraction.subject
            ? String(existingInfraction.subject)
            : String(selectedSubjectId || ""),
          specific_items: parsedItems,
          intervention_applied: existingInfraction.intervention_applied || "verbal",
          date_of_infraction: getLocalDateTimeString(existingInfraction.date_of_infraction),
          detailed_note: existingInfraction.detailed_note || "",
        });
      } else {
        setFormData({
          student: selectedStudentId ? String(selectedStudentId) : "",
          subject: selectedSubjectId ? String(selectedSubjectId) : "",
          specific_items: [],
          intervention_applied: "verbal",
          date_of_infraction: getLocalDateTimeString(),
          detailed_note: "",
        });
      }
    }
  }, [isOpen, existingInfraction, selectedStudentId, selectedSubjectId]);

  useEffect(() => {
    const isOriginalStudent =
      existingInfraction && String(existingInfraction.student) === String(formData.student);

    if (!isOriginalStudent && formData.student && existingTodayInfractions.length > 0) {
      const alreadyRecorded = existingTodayInfractions.some(
        (record) => String(record.student) === String(formData.student)
      );
      setIsDuplicate(alreadyRecorded);
    } else {
      setIsDuplicate(false);
    }
  }, [formData.student, existingTodayInfractions, existingInfraction]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const toggleSpecificItem = (item) => {
    setFormData((prev) => {
      const exists = prev.specific_items.includes(item);
      const updated = exists
        ? prev.specific_items.filter((i) => i !== item)
        : [...prev.specific_items, item];
      return { ...prev, specific_items: updated };
    });
  };

  const handleAddCustomItem = (e) => {
    e.preventDefault();
    const trimmed = customInput.trim();
    if (!trimmed) return;

    if (!availablePresets.includes(trimmed)) {
      setAvailablePresets((prev) => [...prev, trimmed]);
    }

    if (!formData.specific_items.includes(trimmed)) {
      setFormData((prev) => ({
        ...prev,
        specific_items: [...prev.specific_items, trimmed],
      }));
    }

    setCustomInput("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.student || formData.specific_items.length === 0 || isDuplicate || isSubmitting) return;

    setIsSubmitting(true);

    const payload = {
      ...formData,
      specific_item: formData.specific_items.join(", "),
    };
    delete payload.specific_items;

    try {
      if (onSubmit) {
        await onSubmit(payload);
      }

      const successMsg = existingInfraction
        ? "Infraction record updated successfully!"
        : "Uniform infraction logged successfully!";

      if (onShowToast) {
        onShowToast(successMsg, "success");
      }

      onClose();
    } catch (err) {
      console.error("Failed to save infraction record:", err);
      if (onShowToast) {
        onShowToast("Failed to save record. Please try again.", "error");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClear = async () => {
    if (existingInfraction && onClear && !isSubmitting) {
      setIsSubmitting(true);
      try {
        await onClear(existingInfraction.id || existingInfraction);
        if (onShowToast) {
          onShowToast("Infraction record deleted successfully.", "success");
        }
        onClose();
      } catch (err) {
        console.error("Failed to delete record:", err);
        if (onShowToast) {
          onShowToast("Failed to delete record.", "error");
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const isEditing = Boolean(existingInfraction);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={!isSubmitting ? onClose : undefined}
            className="fixed inset-0 bg-black/50 backdrop-blur-xs"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            transition={{ type: "spring", duration: 0.25, bounce: 0.1 }}
            className="relative w-full max-w-lg bg-white rounded-xl shadow-2xl border border-zinc-200 overflow-hidden z-10"
          >
            {/* Header */}
            <div className="px-5 py-4 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <Shirt className="w-5 h-5 text-amber-500" />
                <div>
                  <h3 className="font-bold text-zinc-900 text-sm">
                    {isEditing ? "Edit Uniform Infraction" : "Log Uniform Infraction"}
                  </h3>
                </div>
              </div>
              <button
                type="button"
                disabled={isSubmitting}
                onClick={onClose}
                className="p-1 rounded-md text-zinc-400 hover:text-zinc-700 hover:bg-zinc-200/60 transition-colors cursor-pointer disabled:opacity-40"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-5 space-y-4 text-xs">
              {(isDuplicate || apiError) && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2 text-amber-800">
                  <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                  <div className="text-[11px]">
                    <span className="font-semibold block">Record Already Exists</span>
                    {apiError || "This student already has a uniform infraction recorded for today."}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <CustomSelect
                  label="Student *"
                  options={students}
                  value={formData.student}
                  onChange={(val) => handleChange("student", val)}
                  placeholder="Select Student"
                  icon={User}
                  disabled={isSubmitting}
                />

                <CustomSelect
                  label="Subject (Optional)"
                  options={subjects}
                  value={formData.subject}
                  onChange={(val) => handleChange("subject", val)}
                  placeholder="Select Subject Period"
                  icon={BookOpen}
                  disabled={isSubmitting}
                />
              </div>

              {/* Multi-Select Violation Items */}
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wider flex items-center gap-1.5">
                    <Tag className="w-3 h-3 text-zinc-400" />
                    Specific Violation(s) *
                  </label>
                  <span className="text-[10px] text-zinc-400">
                    {formData.specific_items.length} selected
                  </span>
                </div>

                <div className="p-2.5 bg-zinc-50 border border-zinc-200 rounded-lg space-y-2">
                  <div className="flex flex-wrap gap-1.5 max-h-36 overflow-y-auto pr-1">
                    {availablePresets.map((preset) => {
                      const isSelected = formData.specific_items.includes(preset);
                      return (
                        <button
                          key={preset}
                          type="button"
                          disabled={isSubmitting}
                          onClick={() => toggleSpecificItem(preset)}
                          className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50 ${
                            isSelected
                              ? "bg-amber-500 text-white shadow-xs font-semibold"
                              : "bg-white border border-zinc-200 text-zinc-700 hover:bg-zinc-100"
                          }`}
                        >
                          {isSelected && <Check className="w-3 h-3 text-white" />}
                          <span>{preset}</span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Add Custom Item Input */}
                  <div className="flex items-center gap-1.5 pt-1 border-t border-zinc-200/60">
                    <input
                      type="text"
                      placeholder="Add custom violation..."
                      value={customInput}
                      onChange={(e) => setCustomInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddCustomItem(e);
                        }
                      }}
                      disabled={isSubmitting}
                      className="flex-1 h-7 px-2 bg-white border border-zinc-200 rounded text-[11px] text-zinc-800 focus:outline-none focus:border-zinc-400 disabled:opacity-50"
                    />
                    <button
                      type="button"
                      onClick={handleAddCustomItem}
                      disabled={!customInput.trim() || isSubmitting}
                      className="h-7 px-2 bg-zinc-200 hover:bg-zinc-300 disabled:opacity-40 text-zinc-700 font-medium rounded text-[11px] transition-colors flex items-center gap-1 cursor-pointer"
                    >
                      <Plus className="w-3 h-3" />
                      <span>Add</span>
                    </button>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <CustomSelect
                  label="Intervention Applied *"
                  options={INTERVENTION_CHOICES}
                  value={formData.intervention_applied}
                  onChange={(val) => handleChange("intervention_applied", val)}
                  placeholder="Select Intervention"
                  icon={AlertCircle}
                  disabled={isSubmitting}
                />

                <div className="flex flex-col gap-1">
                  <label className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wider">
                    Date & Time
                  </label>
                  <input
                    type="datetime-local"
                    disabled={isSubmitting}
                    value={formData.date_of_infraction}
                    onChange={(e) =>
                      handleChange("date_of_infraction", e.target.value)
                    }
                    className="w-full h-9 px-3 bg-zinc-50 border border-zinc-200 rounded-lg text-xs font-medium text-zinc-800 focus:outline-none focus:border-zinc-400 disabled:opacity-50"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wider">
                  Detailed Note
                </label>
                <textarea
                  rows="3"
                  disabled={isSubmitting}
                  value={formData.detailed_note}
                  onChange={(e) => handleChange("detailed_note", e.target.value)}
                  placeholder="Provide additional details regarding the infraction..."
                  className="w-full p-2.5 bg-zinc-50 border border-zinc-200 rounded-lg text-xs text-zinc-800 focus:outline-none focus:border-zinc-400 resize-none disabled:opacity-50"
                />
              </div>

              {/* Actions Footer */}
              <div className="pt-3 flex items-center justify-between border-t border-zinc-100">
                <div>
                  {isEditing && (
                    <button
                      type="button"
                      disabled={isSubmitting}
                      onClick={handleClear}
                      className="px-3 h-8 bg-rose-50 hover:bg-rose-100 text-rose-700 font-semibold rounded-lg border border-rose-200 transition-colors flex items-center gap-1.5 cursor-pointer disabled:opacity-40"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      <span>Delete</span>
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={isSubmitting}
                    onClick={onClose}
                    className="px-4 h-8 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 font-medium rounded-lg transition-colors cursor-pointer disabled:opacity-40"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={
                      !formData.student ||
                      formData.specific_items.length === 0 ||
                      isDuplicate ||
                      isSubmitting
                    }
                    className="px-4 h-8 bg-[#002855] hover:bg-blue-900 disabled:opacity-40 text-white font-medium rounded-lg transition-colors shadow-xs cursor-pointer flex items-center gap-1.5 min-w-[110px] justify-center"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        <span>Saving...</span>
                      </>
                    ) : isEditing ? (
                      "Update Record"
                    ) : (
                      "Save Record"
                    )}
                  </button>
                </div>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default LogUniformInfractionModal;